/**
 * Field Requests API Client
 *
 * Purpose: Equipment/trip requests from mobile portal; Ketchup portal approves/rejects.
 * Location: packages/api-client/src/ketchup/fieldRequestsAPI.ts
 */

import { apiClient } from './api';

const BASE = '/field-requests';

export type FieldRequestType = 'equipment' | 'trip' | 'other' | 'agent_visit';
export type FieldRequestStatus = 'pending' | 'approved' | 'rejected';
/** Reason for agent_visit: top-up, POS servicing/replacement, training, support */
export type AgentVisitReason = 'top_up' | 'pos_servicing' | 'pos_replacement' | 'training' | 'support';

export interface FieldRequest {
  id: string;
  type: FieldRequestType;
  mobileUnitId: string;
  requestedByDriverId: string | null;
  agentId: string | null;
  requestReason: string | null;
  payload: Record<string, unknown>;
  status: FieldRequestStatus;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const fieldRequestsAPI = {
  async create(body: {
    type: FieldRequestType;
    mobileUnitId: string;
    requestedByDriverId?: string;
    agentId?: string;
    requestReason?: string;
    payload?: Record<string, unknown>;
  }): Promise<FieldRequest> {
    return apiClient.post<FieldRequest>(BASE, body);
  },

  async list(filters?: {
    mobileUnitId?: string;
    status?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<FieldRequest[]> {
    const data = await apiClient.get<FieldRequest[]>(BASE, filters as Record<string, unknown>);
    return Array.isArray(data) ? data : [];
  },

  async getById(id: string): Promise<FieldRequest | null> {
    const data = await apiClient.get<FieldRequest>(`${BASE}/${id}`).catch(() => null);
    return data ?? null;
  },

  async approve(id: string, reviewNotes?: string, reviewedBy?: string): Promise<FieldRequest> {
    return apiClient.patch<FieldRequest>(`${BASE}/${id}`, {
      status: 'approved',
      reviewNotes,
      reviewedBy,
    });
  },

  async reject(id: string, reviewNotes?: string, reviewedBy?: string): Promise<FieldRequest> {
    return apiClient.patch<FieldRequest>(`${BASE}/${id}`, {
      status: 'rejected',
      reviewNotes,
      reviewedBy,
    });
  },
};
